<template>
  <div>
    <v-container fluid>
      <v-card class="mb-4">
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-file-chart</v-icon>
          Report Generator
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" class="pb-0">
              <p class="subtitle-1 text--secondary">
                Generate custom reports based on case files with various
                filtering and export options.
              </p>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Main Content -->
      <v-card outlined>
        <v-tabs v-model="activeTab" background-color="transparent" grow>
          <v-tab>
            <v-icon left>mdi-cog</v-icon>
            Configure
          </v-tab>
          <v-tab>
            <v-icon left>mdi-eye</v-icon>
            Preview
          </v-tab>
          <v-tab>
            <v-icon left>mdi-download</v-icon>
            Export
          </v-tab>
        </v-tabs>

        <v-divider></v-divider>

        <v-tabs-items v-model="activeTab">
          <!-- Configuration Tab -->
          <v-tab-item>
            <v-card-text>
              <!-- Template Selection -->
              <v-card outlined class="mb-4">
                <v-card-title class="subtitle-1">
                  <v-icon left>mdi-file-document-outline</v-icon>
                  Report Template
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-select
                            v-model="selectedTemplate"
                            :items="reportTemplates"
                            item-text="text"
                            item-value="value"
                            label="Select Template"
                            outlined
                            dense
                            @change="loadTemplate"
                            clearable
                            v-bind="attrs"
                            v-on="on"
                          >
                            <template v-slot:selection="{ item }">
                              <v-icon small left :color="item.color">{{
                                item.icon
                              }}</v-icon>
                              {{ item.text }}
                            </template>
                            <template v-slot:item="{ item }">
                              <v-icon small left :color="item.color">{{
                                item.icon
                              }}</v-icon>
                              <span>{{ item.text }}</span>
                              <small class="ml-2 text--secondary">{{
                                item.description
                              }}</small>
                            </template>
                          </v-select>
                        </template>
                        <span>Select a pre-configured report template</span>
                      </v-tooltip>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="reportName"
                        label="Report Name"
                        outlined
                        dense
                        placeholder="Enter a name for your report"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <!-- Add template description when one is selected -->
                  <v-row v-if="selectedTemplate && getTemplateDescription">
                    <v-col cols="12">
                      <v-alert
                        text
                        dense
                        color="info"
                        icon="mdi-information-outline"
                        border="left"
                      >
                        {{ getTemplateDescription }}
                      </v-alert>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Filters -->
              <v-card outlined class="mb-4">
                <v-card-title class="subtitle-1">
                  <v-icon left>mdi-filter-variant</v-icon>
                  Filters
                  <v-spacer></v-spacer>
                  <v-btn small text color="error" @click="clearFilters">
                    <v-icon left>mdi-filter-remove</v-icon>
                    Clear Filters
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-menu
                        ref="startMenu"
                        v-model="startMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="filters.startDate"
                            label="Start Date"
                            prepend-icon="mdi-calendar"
                            readonly
                            outlined
                            dense
                            v-bind="attrs"
                            v-on="on"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="filters.startDate"
                          @input="startMenu = false"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-menu
                        ref="endMenu"
                        v-model="endMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="filters.endDate"
                            label="End Date"
                            prepend-icon="mdi-calendar"
                            readonly
                            outlined
                            dense
                            v-bind="attrs"
                            v-on="on"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="filters.endDate"
                          @input="endMenu = false"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-autocomplete
                        v-model="filters.insurers"
                        :items="insurers"
                        item-text="name"
                        item-value="id"
                        label="Insurers"
                        outlined
                        dense
                        chips
                        multiple
                        small-chips
                        clearable
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-autocomplete
                        v-model="filters.departments"
                        :items="refTypes"
                        item-text="name"
                        item-value="id"
                        label="Departments"
                        outlined
                        dense
                        chips
                        multiple
                        small-chips
                        clearable
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="filters.status"
                        :items="statusOptions"
                        item-text="text"
                        item-value="value"
                        label="Status"
                        outlined
                        dense
                        chips
                        multiple
                        small-chips
                        clearable
                      ></v-select>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-autocomplete
                        v-model="filters.branch"
                        :items="branches"
                        item-text="name"
                        item-value="id"
                        label="Branch"
                        outlined
                        dense
                        chips
                        multiple
                        small-chips
                        clearable
                        placeholder="Select branches"
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-autocomplete
                        v-model="filters.adjuster"
                        :items="adjustersOnly"
                        item-text="fullname"
                        item-value="id"
                        label="Adjuster"
                        outlined
                        dense
                        chips
                        multiple
                        small-chips
                        clearable
                        placeholder="Select adjusters"
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-autocomplete
                        v-model="filters.editor"
                        :items="editorsOnly"
                        item-text="fullname"
                        item-value="id"
                        label="Editor"
                        outlined
                        dense
                        chips
                        multiple
                        small-chips
                        clearable
                        placeholder="Select editors"
                      ></v-autocomplete>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-autocomplete
                        v-model="filters.vehicleNo"
                        :items="vehicleNumbers"
                        label="Vehicle Number"
                        outlined
                        dense
                        chips
                        multiple
                        small-chips
                        clearable
                        prepend-icon="mdi-car"
                        placeholder="Select vehicle numbers"
                        :filter="customVehicleFilter"
                      ></v-autocomplete>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                        v-model="filters.keyword"
                        label="Keyword Search"
                        outlined
                        dense
                        clearable
                        prepend-icon="mdi-magnify"
                        hint="Search in case ID, claim number, insurer comments"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Column Selection -->
              <v-card outlined class="mb-4">
                <v-card-title class="subtitle-1 d-flex justify-space-between">
                  <div>
                    <v-icon left>mdi-table-column</v-icon>
                    Columns
                  </div>
                  <v-btn small text @click="selectAllColumns">
                    {{ allColumnsSelected ? "Deselect All" : "Select All" }}
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col
                      v-for="column in availableColumns"
                      :key="column.value"
                      cols="12"
                      sm="6"
                      md="4"
                      lg="3"
                    >
                      <v-checkbox
                        v-model="selectedColumns"
                        :label="column.text"
                        :value="column.value"
                        dense
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Sorting and Grouping -->
              <v-card outlined class="mb-4">
                <v-card-title class="subtitle-1">
                  <v-icon left>mdi-sort-variant</v-icon>
                  Sorting & Grouping
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="sortBy"
                        :items="sortingOptions"
                        label="Sort By"
                        outlined
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="sortDirection"
                        :items="['Ascending', 'Descending']"
                        label="Sort Direction"
                        outlined
                      ></v-select>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="groupBy"
                        :items="sortingOptions"
                        label="Group By"
                        outlined
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-switch
                        v-model="showSubtotals"
                        label="Show Subtotals"
                        :disabled="!groupBy"
                      ></v-switch>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Actions -->
              <v-card-actions class="justify-end">
                <v-btn
                  color="primary"
                  @click="generatePreview"
                  :loading="loading"
                >
                  Generate Preview
                </v-btn>
              </v-card-actions>
            </v-card-text>
          </v-tab-item>

          <!-- Preview Tab -->
          <v-tab-item>
            <v-card-text>
              <v-data-table
                :headers="previewHeaders"
                :items="previewData"
                :loading="loading"
                :items-per-page="10"
                class="elevation-1"
              >
                <template v-slot:no-data>
                  Configure and generate a report to see preview
                </template>
              </v-data-table>
            </v-card-text>
          </v-tab-item>

          <!-- Export Tab -->
          <v-tab-item>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6" lg="4">
                  <v-card outlined>
                    <v-card-title class="subtitle-1">
                      <v-icon left>mdi-file-export</v-icon>
                      Export Options
                    </v-card-title>
                    <v-card-text>
                      <p class="subtitle-2 mb-2">File Format</p>
                      <v-radio-group v-model="exportFormat" class="mt-0">
                        <v-radio
                          v-for="format in exportFormats"
                          :key="format.value"
                          :label="format.label"
                          :value="format.value"
                          color="primary"
                        ></v-radio>
                      </v-radio-group>

                      <!-- Add Page Orientation Options -->
                      <v-divider class="my-3"></v-divider>
                      <p class="subtitle-2 mb-2 d-flex align-center">
                        Page Orientation
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              small
                              color="grey lighten-1"
                              class="ml-2"
                              v-bind="attrs"
                              v-on="on"
                            >
                              mdi-information-outline
                            </v-icon>
                          </template>
                          <span
                            >Orientation applies to PDF and Word formats
                            only</span
                          >
                        </v-tooltip>
                      </p>
                      <v-radio-group
                        v-model="exportOptions.orientation"
                        row
                        class="mt-0"
                        :disabled="!['pdf', 'word'].includes(exportFormat)"
                      >
                        <v-radio
                          label="Portrait"
                          value="portrait"
                          color="primary"
                        ></v-radio>
                        <v-radio
                          label="Landscape"
                          value="landscape"
                          color="primary"
                        ></v-radio>
                      </v-radio-group>

                      <v-divider class="my-3"></v-divider>
                      <p class="subtitle-2 mb-2">Reference Format</p>
                      <v-radio-group
                        v-model="exportOptions.showFullRef"
                        row
                        class="mt-0"
                      >
                        <v-radio
                          label="Full AASB REF"
                          :value="true"
                          color="primary"
                        ></v-radio>
                        <v-radio
                          label="Casefile ID Only"
                          :value="false"
                          color="primary"
                        ></v-radio>
                      </v-radio-group>

                      <v-divider class="my-3"></v-divider>
                      <v-btn
                        color="primary"
                        block
                        :disabled="!previewData.length"
                        @click="exportReport"
                        :loading="exporting"
                      >
                        <v-icon left>mdi-download</v-icon>
                        Export Report
                      </v-btn>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </v-container>

    <!-- Material You style snackbar with rounded corners -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="snackbarTimeout"
      bottom
      rounded="pill"
      elevation="4"
      max-width="400"
    >
      <div class="d-flex align-center px-2">
        <v-icon left>{{ snackbarIcon }}</v-icon>
        <span class="font-weight-medium">{{ snackbarText }}</span>
        <v-spacer></v-spacer>
        <v-btn
          x-small
          fab
          :color="snackbarActionColor"
          class="ml-2"
          @click="handleSnackbarAction"
        >
          <v-icon color="black">{{
            snackbarAction === "close" ? "mdi-close" : "mdi-refresh"
          }}</v-icon>
        </v-btn>
      </div>
    </v-snackbar>
  </div>
</template>

<script>
import moment from "moment";
import CasefileDataService from "@/services/CasefileDataService";
import InsDataService from "@/services/InsDataService";
import DeptDataService from "@/services/DeptDataService";
import StagesDataService from "@/services/StageDataService";
import SubRefTypeDataService from "@/services/SubDeptDataService";
import BranchDataService from "@/services/BranchDataService";
import HandlerDataService from "@/services/HandlerDataService";
import StateDataService from "@/services/StateDataService";
import MemberDataService from "@/services/MemberDataService";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export default {
  name: "Report",
  data: () => ({
    activeTab: 0,
    loading: false,
    exporting: false,
    startMenu: false,
    endMenu: false,
    // Template Management
    selectedTemplate: null,
    reportName: "",
    // Filters
    filters: {
      startDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
      insurers: [], // Change from null to empty array
      departments: [], // Change from null to empty array
      status: [], // Change from null to empty array
      branch: [], // Change from null to empty array
      adjuster: [], // Change from null to empty array
      editor: [], // Change from null to empty array
      vehicleNo: [], // Change from string to empty array
      keyword: "",
    },
    // Column Management
    selectedColumns: [
      "id",
      "vehicleNo",
      "insurer",
      "dateOfAssign",
      "fileStatus",
    ],
    // Sorting and Grouping
    sortBy: "dateOfAssign",
    sortDirection: "Descending",
    groupBy: null,
    showSubtotals: false,
    // Export Options
    exportFormat: "pdf",
    exportOptions: {
      orientation: "portrait", // Default to portrait
      showFullRef: true, // New option to toggle between full AASB REF and simple ID
    },
    // Data
    insurers: [],
    statusOptions: [],
    previewData: [],
    // Templates
    reportTemplates: [
      {
        text: "Default Template",
        value: "default",
        icon: "mdi-file-document-outline",
        color: "primary",
        description: "Basic template with standard columns and sorting by date",
      },
      {
        text: "Monthly Status Report",
        value: "monthly",
        icon: "mdi-calendar-month",
        color: "green",
        description:
          "Shows cases by status with days running, grouped by current status",
      },
      {
        text: "Insurer Performance Report",
        value: "insurer",
        icon: "mdi-chart-bar",
        color: "blue",
        description:
          "Analysis of cases by insurer with additional performance metrics",
      },
      {
        text: "Department Summary",
        value: "department",
        icon: "mdi-office-building",
        color: "orange",
        description: "Overview of cases by department with workflow statistics",
      },
    ],
    // Export Formats
    exportFormats: [
      { label: "PDF Document", value: "pdf" },
      { label: "Word Document", value: "word" },
      { label: "Excel Spreadsheet", value: "excel" },
      { label: "CSV File", value: "csv" },
    ],
    // Add new data properties for reference data
    refTypes: [],
    subRefTypes: [],
    branches: [],
    handlers: [],
    states: [],
    stages: [],
    users: [],
    vehicleNumbers: [], // Add this to store available vehicle numbers
    // Add snackbar properties for alerts
    snackbar: false,
    snackbarText: "",
    snackbarColor: "info",
    snackbarTimeout: 3000,
    // Modern snackbar properties
    snackbarIcon: "mdi-information-outline",
    snackbarAction: "close",
    snackbarActionColor: "white",
    snackbarCallback: null,
  }),
  computed: {
    availableColumns() {
      return [
        { text: "Case ID", value: "id" },
        { text: "Vehicle Number", value: "vehicleNo" },
        { text: "Department", value: "deptName" },
        { text: "Sub Department", value: "subDeptCode" },
        { text: "Branch", value: "branchCode" },
        { text: "Insurer", value: "insurerName" },
        { text: "Handler", value: "handlerName" },
        { text: "Date Assigned", value: "dateOfAssign" },
        { text: "Date of Loss", value: "dateOfLoss" },
        { text: "Status", value: "statusName" },
        { text: "Claim Number", value: "claimNo" },
        { text: "Days Running", value: "days" },
        { text: "Adjuster", value: "adjusterName" },
        { text: "Editor", value: "editorName" },
        { text: "Clerk", value: "clerkName" },
        { text: "State", value: "stateName" },
      ];
    },
    previewHeaders() {
      return this.availableColumns.filter((col) =>
        this.selectedColumns.includes(col.value)
      );
    },
    allColumnsSelected() {
      return this.selectedColumns.length === this.availableColumns.length;
    },
    sortingOptions() {
      // Map selected columns to display names
      return this.selectedColumns.map((value) => {
        const displayNames = {
          id: "Case ID",
          vehicleNo: "Vehicle Number",
          insurer: "Insurer",
          dateOfAssign: "Date Of Assign",
          fileStatus: "File Status",
        };
        return {
          text:
            displayNames[value] ||
            this.availableColumns.find((col) => col.value === value)?.text ||
            value,
          value: value,
        };
      });
    },
    // Fix the adjustersOnly computed property
    adjustersOnly() {
      if (!this.users || !Array.isArray(this.users)) {
        return [];
      }
      return this.users.filter((user) => {
        // Check if user has roles property and it's an array
        if (!user.roles) {
          return false;
        }
        // Handle roles as array of strings or array of objects
        if (Array.isArray(user.roles)) {
          if (typeof user.roles[0] === "string") {
            // If roles is array of strings
            return user.roles.includes("ADJUSTER");
          } else if (typeof user.roles[0] === "object") {
            // If roles is array of objects with roleCode property
            return user.roles.some(
              (role) =>
                role.roleCode && role.roleCode.toUpperCase() === "ADJUSTER"
            );
          }
        }
        return false;
      });
    },

    // Fix the editorsOnly computed property to include all editor-related roles
    editorsOnly() {
      if (!this.users || !Array.isArray(this.users)) {
        return [];
      }
      return this.users.filter((user) => {
        // Check if user has roles property
        if (!user.roles) {
          return false;
        }
        // Now check for any roles that should have editor capabilities
        const editorRoles = ["EDITOR", "BRANCHMANAGER"];
        // Handle roles as array of strings or array of objects
        if (Array.isArray(user.roles)) {
          // If roles is array of strings
          if (typeof user.roles[0] === "string") {
            return user.roles.some((role) =>
              editorRoles.includes(role.toUpperCase())
            );
          }
          // If roles is array of objects with roleCode property
          else if (typeof user.roles[0] === "object") {
            return user.roles.some((role) => {
              if (!role || !role.roleCode) return false;
              return editorRoles.includes(role.roleCode.toUpperCase());
            });
          }
        }
        return false;
      });
    },
    // Add a new computed property for template description
    getTemplateDescription() {
      if (!this.selectedTemplate) return null;
      const template = this.reportTemplates.find(
        (t) => t.value === this.selectedTemplate
      );
      return template ? template.description : null;
    },
  },
  methods: {
    // Modern showAlert method with action support
    showAlert(text, type = "info", action = "close", callback = null) {
      this.snackbarText = text;
      // Map alert types to material design colors
      switch (type) {
        case "success":
          this.snackbarColor = "success";
          this.snackbarIcon = "mdi-check-circle";
          break;
        case "error":
          this.snackbarColor = "error";
          this.snackbarIcon = "mdi-alert-circle";
          break;
        case "warning":
          this.snackbarColor = "warning";
          this.snackbarIcon = "mdi-alert";
          break;
        default:
          this.snackbarColor = "primary";
          this.snackbarIcon = "mdi-information-outline";
      }
      // Set action type (close or retry)
      this.snackbarAction = action;
      this.snackbarActionColor = type === "error" ? "error" : "white";
      this.snackbarCallback = callback;

      this.snackbar = true;
    },

    // Handle snackbar action button clicks
    handleSnackbarAction() {
      if (
        this.snackbarAction === "retry" &&
        typeof this.snackbarCallback === "function"
      ) {
        // Execute callback if action is retry
        this.snackbarCallback();
      }
      // Close the snackbar
      this.snackbar = false;
    },

    async loadData() {
      try {
        const [
          insurers,
          departments,
          subDepts,
          branches,
          handlers,
          states,
          stages,
          users,
          casefiles, // Add this to get vehicle numbers
        ] = await Promise.all([
          InsDataService.getAll(),
          DeptDataService.getAll(),
          SubRefTypeDataService.getAll(),
          BranchDataService.getAll(),
          HandlerDataService.getAll(),
          StateDataService.getAll(),
          StagesDataService.getAll(),
          MemberDataService.getAll(),
          CasefileDataService.getAll(), // Add this call to get all case files for vehicle numbers
        ]);

        // Store all reference data
        this.insurers = insurers.data;
        this.refTypes = departments.data;
        this.subRefTypes = subDepts.data;
        this.branches = branches.data;
        this.handlers = handlers.data;
        this.states = states.data;
        this.stages = stages.data;
        this.users = users.data;

        // Extract unique vehicle numbers for the autocomplete
        const vehicleNos = casefiles.data
          .map((file) => file.vehicleNo)
          .filter(Boolean) // Remove nulls and empty strings
          .filter((value, index, self) => self.indexOf(value) === index); // Get unique values
        this.vehicleNumbers = vehicleNos;

        // Update status options for filters
        this.statusOptions = this.stages.map((stage) => ({
          text: stage.name,
          value: stage.stageCode,
        }));
      } catch (error) {
        console.error("Error loading data:", error);
        this.showAlert("Error loading report configuration data", "error");
      }
    },
    selectAllColumns() {
      if (this.allColumnsSelected) {
        this.selectedColumns = [];
      } else {
        this.selectedColumns = this.availableColumns.map((col) => col.value);
      }
    },
    loadTemplate(templateValue) {
      // Implementation for loading templates
      const templates = {
        default: {
          columns: ["id", "vehicleNo", "insurer", "dateOfAssign", "fileStatus"],
          sortBy: "dateOfAssign",
          sortDirection: "Descending",
          groupBy: null,
          filters: {
            // Reset filters to default
            startDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
            endDate: moment().format("YYYY-MM-DD"),
            insurers: [],
            departments: [],
            status: [],
            branch: [],
            adjuster: [],
            editor: [],
            vehicleNo: [],
            keyword: "",
          },
        },
        monthly: {
          columns: [
            "id",
            "vehicleNo",
            "insurerName",
            "handlerName",
            "dateOfAssign",
            "statusName",
            "dateOfLoss",
            "days",
            "adjusterName",
          ],
          sortBy: "days",
          sortDirection: "Descending",
          groupBy: "statusName",
          showSubtotals: true,
          filters: {
            // Keep current date range but reset other filters
            startDate: this.filters.startDate,
            endDate: this.filters.endDate,
            insurers: [],
            departments: [],
            status: [],
            branch: [],
            adjuster: [],
            editor: [],
            vehicleNo: [],
            keyword: "",
          },
        },
        insurer: {
          columns: [
            "id",
            "vehicleNo",
            "insurerName",
            "handlerName",
            "dateOfAssign",
            "dateOfLoss",
            "statusName",
            "days",
            "adjusterName",
          ],
          sortBy: "insurerName",
          sortDirection: "Ascending",
          groupBy: "insurerName",
          showSubtotals: true,
          filters: {
            // Keep current date range but reset other filters
            startDate: this.filters.startDate,
            endDate: this.filters.endDate,
            insurers: [],
            departments: [],
            status: [],
            branch: [],
            adjuster: [],
            editor: [],
            vehicleNo: [],
            keyword: "",
          },
        },
        department: {
          columns: [
            "id",
            "vehicleNo",
            "deptName",
            "subDeptCode",
            "branchCode",
            "insurerName",
            "dateOfAssign",
            "statusName",
            "days",
            "adjusterName",
            "editorName",
          ],
          sortBy: "deptName",
          sortDirection: "Ascending",
          groupBy: "deptName",
          showSubtotals: true,
          filters: {
            // Keep current date range but reset other filters
            startDate: this.filters.startDate,
            endDate: this.filters.endDate,
            insurers: [],
            departments: [],
            status: [],
            branch: [],
            adjuster: [],
            editor: [],
            vehicleNo: [],
            keyword: "",
          },
        },
      };

      const template = templates[templateValue];
      if (template) {
        // Set columns
        this.selectedColumns = template.columns;

        // Set sorting
        this.sortBy = template.sortBy;
        this.sortDirection = template.sortDirection;

        // Set grouping
        this.groupBy = template.groupBy;
        this.showSubtotals = template.showSubtotals || false;

        // Set filters
        if (template.filters) {
          // Preserve date range by default but apply template's date range if specified
          const preserveDateRange =
            !template.filters.startDate || !template.filters.endDate;

          this.filters = {
            ...template.filters,
            // Only preserve current date range if template doesn't specify its own
            startDate: preserveDateRange
              ? this.filters.startDate
              : template.filters.startDate,
            endDate: preserveDateRange
              ? this.filters.endDate
              : template.filters.endDate,
          };
        }

        // Always update report name when template changes
        const templateInfo = this.reportTemplates.find(
          (t) => t.value === templateValue
        );
        if (templateInfo) {
          // Use consistent date format to avoid warnings
          const currentDate = moment().format("DD/MM/YYYY");
          this.reportName = `${templateInfo.text} - ${currentDate}`;
        }

        // Show a message about the template being applied
        this.showAlert(
          `Applied template: ${
            this.reportTemplates.find((t) => t.value === templateValue)?.text ||
            templateValue
          }`,
          "info"
        );
      }
    },

    async generatePreview() {
      this.loading = true;
      try {
        const response = await CasefileDataService.getAll();
        let data = response.data; // Remove the filter for active cases to show all
        // Apply filters
        data = this.applyFilters(data);
        data = this.formatData(data);
        // Apply sorting
        data = this.applySorting(data);
        // Apply grouping if needed
        if (this.groupBy) {
          data = this.applyGrouping(data);
        }
        this.previewData = data;
        this.activeTab = 1;
      } catch (error) {
        console.error("Error generating preview:", error);
        this.showAlert("Error generating report preview", "error");
      } finally {
        this.loading = false;
      }
    },
    applyFilters(data) {
      return data.filter((item) => {
        // Date range filter with safe parsing
        const itemDate = this.parseDate(item.dateOfAssign);
        const startDate = this.parseDate(this.filters.startDate);
        const endDate = this.parseDate(this.filters.endDate);
        if (itemDate && startDate && endDate) {
          if (!itemDate.isBetween(startDate, endDate, "day", "[]")) {
            return false;
          }
        }
        // Insurers filter
        if (
          this.filters.insurers.length &&
          !this.filters.insurers.includes(item.insurer)
        ) {
          return false;
        }
        // Departments filter
        if (
          this.filters.departments.length &&
          !this.filters.departments.includes(item.refType)
        ) {
          return false;
        }
        // Status filter
        if (
          this.filters.status.length &&
          !this.filters.status.includes(item.fileStatus)
        ) {
          return false;
        }
        // Branch filter - updated for multiple selection
        if (
          this.filters.branch.length &&
          !this.filters.branch.includes(item.branch)
        ) {
          return false;
        }
        // Adjuster filter - updated for multiple selection
        if (
          this.filters.adjuster.length &&
          !this.filters.adjuster.includes(item.adjuster)
        ) {
          return false;
        }
        // Editor filter - updated for multiple selection
        if (
          this.filters.editor.length &&
          !this.filters.editor.includes(item.editor)
        ) {
          return false;
        }
        // Vehicle number filter - updated for multiple selection
        if (this.filters.vehicleNo.length) {
          // If any vehicle number filter is selected, check if the item matches any of them
          return this.filters.vehicleNo.some((vehicleNo) =>
            item.vehicleNo?.toLowerCase().includes(vehicleNo.toLowerCase())
          );
        }
        // Keyword search
        if (this.filters.keyword) {
          const keyword = this.filters.keyword.toLowerCase();
          const searchFields = [
            item.id?.toString(),
            item.vehicleNo,
            item.claimNo,
            item.insComment,
          ].filter(Boolean);
          return searchFields.some((field) =>
            field.toLowerCase().includes(keyword)
          );
        }
        return true;
      });
    },
    clearFilters() {
      this.filters = {
        startDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
        insurers: [],
        departments: [],
        status: [],
        branch: [],
        adjuster: [],
        editor: [],
        vehicleNo: [],
        keyword: "",
      };
    },
    applySorting(data) {
      return [...data].sort((a, b) => {
        let aVal = a[this.sortBy];
        let bVal = b[this.sortBy];
        // Handle special cases
        if (this.sortBy === "days") {
          aVal = moment().diff(moment(a.dateOfAssign), "days");
          bVal = moment().diff(moment(b.dateOfAssign), "days");
        }
        // Handle dates
        if (moment(aVal, moment.ISO_8601, true).isValid()) {
          aVal = moment(aVal).valueOf();
          bVal = moment(bVal).valueOf();
        }
        if (this.sortDirection === "Ascending") {
          return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
      });
    },
    applyGrouping(data) {
      // Implementation for grouping data
      const groups = {};
      data.forEach((item) => {
        const groupValue = item[this.groupBy];
        if (!groups[groupValue]) {
          groups[groupValue] = [];
        }
        groups[groupValue].push(item);
      });
      // Convert groups back to array with subtotal rows if enabled
      let result = [];
      Object.entries(groups).forEach(([groupValue, items]) => {
        if (this.showSubtotals) {
          // Create an enhanced subtotal object with required properties
          result.push({
            isSubtotal: true,
            groupValue,
            count: items.length,
            id: `subtotal-${groupValue}`, // Add an ID to prevent "AA" showing up
            vehicleNo: "", // Add empty vehicleNo to prevent errors
            // Add the groupBy field with the group value for better display
            [this.groupBy]: groupValue,
            // Add a descriptive subtotal label that will be shown in reports
            subtotalLabel: `${this.getGroupByLabel()} Subtotal: ${groupValue} (${
              items.length
            } items)`,
          });
        }
        result.push(...items);
      });
      return result;
    },

    // Add a new helper method to get a human-friendly label for the group field
    getGroupByLabel() {
      const field = this.groupBy;
      const labels = {
        insurerName: "Insurer",
        deptName: "Department",
        statusName: "Status",
        branchCode: "Branch",
        adjusterName: "Adjuster",
        vehicleNo: "Vehicle",
        // Add other fields as needed
      };
      return labels[field] || field;
    },

    async exportReport() {
      if (!this.previewData.length) return;
      this.exporting = true;
      try {
        switch (this.exportFormat) {
          case "pdf":
            await this.exportToPdf();
            break;
          case "word":
            await this.exportToWord();
            break;
          case "excel":
            await this.exportToExcel();
            break;
          case "csv":
            await this.exportToCsv();
            break;
        }
      } catch (error) {
        console.error("Export error:", error);
        this.showAlert("Error exporting report", "error");
      } finally {
        this.exporting = false;
      }
    },
    async exportToWord() {
      // Add index column to Word export with conditional label
      const refLabel = this.exportOptions.showFullRef ? "AASB REF" : "Case ID";
      const headers = [
        "No.",
        refLabel,
        ...this.previewHeaders
          .filter((h) => h.value !== "id")
          .map((h) => h.text),
      ];
      const tableRows = this.previewData.map((row, index) => {
        const rowData = [index + 1, this.getFormattedRef(row)]; // Use helper method
        this.previewHeaders.forEach((header) => {
          if (header.value === "id") return; // Skip 'id' as we're using it for AASB REF
          const value = row[header.value];
          // Use the same safe date formatting for Word exports
          if (header.value.includes("date") || header.value.includes("Date")) {
            rowData.push(this.getSafeDateFormat(value));
          } else {
            rowData.push(value?.toString() || "");
          }
        });
        return rowData;
      });

      // Create HTML content for Word with proper orientation in the style tag
      // Word uses specific XML tags for orientation settings
      const htmlContent = `
        <html xmlns:o='urn:schemas-microsoft-com:office:office'
              xmlns:w='urn:schemas-microsoft-com:office:word'
              xmlns:m='http://schemas.microsoft.com/office/2004/12/omml'>
        <head>
          <meta charset="utf-8">
          <title>${this.reportName || "Report"}</title>
          <style>
            /* Word-specific styles for page orientation */
            @page WordSection1 {
              size: ${this.exportOptions.orientation};
              margin: 1.0in 0.75in 1.0in 0.75in;
              mso-page-orientation: ${this.exportOptions.orientation};
            }
            div.WordSection1 {
              page: WordSection1;
            }
            /* General styles */
            body {
              font-family: Arial, sans-serif;
              font-size: ${
                this.exportOptions.orientation === "landscape" ? "11pt" : "10pt"
              };
            }
            h1 {
              font-size: 16pt;
              margin-bottom: 12pt;
              font-weight: bold;
            }
            p {
              margin-bottom: 12pt;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
            }
            th, td {
              border: 1px solid #000;
              padding: ${
                this.exportOptions.orientation === "landscape" ? "6px" : "4px"
              };
              text-align: left;
              vertical-align: top;
              word-wrap: break-word;
            }
            th {
              background-color: #4285f4;
              color: white;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: #f5f5f5;
            }
          </style>
          <!-- Word XML configuration to ensure orientation is applied -->
          <xml>
            <w:WordDocument>
              <w:View>Print</w:View>
              <w:Zoom>100</w:Zoom>
              <w:DoNotOptimizeForBrowser/>
            </w:WordDocument>
          </xml>
        </head>
        <body>
          <!-- WordSection1 div is required for Word to apply the page settings -->
          <div class="WordSection1">
            <h1>${this.reportName || "Case Files Report"}</h1>
            <p>Date Range: ${moment(this.filters.startDate).format(
              "DD/MM/YYYY"
            )} - ${moment(this.filters.endDate).format("DD/MM/YYYY")}</p>
            
            <table>
              <thead>
                <tr>
                  ${headers.map((header) => `<th>${header}</th>`).join("")}
                </tr>
              </thead>
              <tbody>
                ${tableRows
                  .map(
                    (row) => `
                  <tr>
                    ${row.map((cell) => `<td>${cell}</td>`).join("")}
                  </tr>
                `
                  )
                  .join("")}
              </tbody>
            </table>
          </div>
        </body>
        </html>
      `;

      // Create blob with more specific Word MIME type
      const blob = new Blob([htmlContent], {
        type: "application/vnd.ms-word;charset=utf-8",
      });
      const fileName = `${this.reportName || "report"}_${moment().format(
        "YYYYMMDD"
      )}.doc`;
      saveAs(blob, fileName);
    },
    async exportToPdf() {
      try {
        // Create PDF with selected orientation
        const doc = new jsPDF({
          orientation: this.exportOptions.orientation,
          unit: "mm",
        });
        doc.setFontSize(16);
        doc.text(this.reportName || "Case Files Report", 14, 15);
        doc.setFontSize(10);
        doc.text(
          `Date Range: ${moment(this.filters.startDate).format(
            "DD/MM/YYYY"
          )} - ${moment(this.filters.endDate).format("DD/MM/YYYY")}`,
          14,
          25
        );

        // Add No. (index) and AASB REF columns with conditional label
        const refLabel = this.exportOptions.showFullRef
          ? "AASB REF"
          : "Case ID";
        const tableHeaders = ["No.", refLabel];
        // Add other headers after the AASB REF
        this.previewHeaders.forEach((h) => {
          if (h.value !== "id") {
            tableHeaders.push(h.text);
          }
        });

        // Add index and AASB REF to each row
        const tableData = this.previewData.map((row, index) => {
          // Special handling for subtotal rows
          if (row.isSubtotal) {
            const subtotalRow = [
              "", // Empty index for subtotal rows
              row.subtotalLabel ||
                `Group: ${row.groupValue} (${row.count} items)`,
            ];

            // Add empty cells for other columns except for the grouped column
            this.previewHeaders.forEach((header) => {
              if (header.value !== "id") {
                subtotalRow.push(
                  header.value === this.groupBy ? row.groupValue : ""
                );
              }
            });
            return subtotalRow;
          }

          // Normal row handling
          const rowData = [
            index + 1, // No. column
            this.getFormattedRef(row),
          ];

          // ...existing row formatting code...
          this.previewHeaders.forEach((header) => {
            if (header.value === "id") return; // Skip 'id' as we're using it for AASB REF
            const value = row[header.value];
            // Special handling for date fields to prevent invalid date
            if (
              header.value.includes("date") ||
              header.value.includes("Date")
            ) {
              rowData.push(this.getSafeDateFormat(value));
            } else if (header.value === "vehicleNo") {
              rowData.push(value?.toUpperCase() || "");
            } else if (header.value === "days") {
              rowData.push(value?.toString() || "0");
            } else {
              rowData.push(value?.toString() || "");
            }
          });
          return rowData;
        });

        // Adjust font size and padding based on orientation
        const fontSize = this.exportOptions.orientation === "landscape" ? 8 : 7;
        const cellPadding =
          this.exportOptions.orientation === "landscape" ? 2 : 1;

        autoTable(doc, {
          head: [tableHeaders],
          body: tableData,
          startY: 30,
          theme: "grid",
          styles: {
            fontSize: fontSize,
            cellPadding: cellPadding,
          },
          headStyles: {
            fillColor: [41, 98, 255],
            textColor: [255, 255, 255],
            fontStyle: "bold",
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245],
          },
        });

        const fileName = `${this.reportName || "report"}_${moment().format(
          "YYYYMMDD"
        )}.pdf`;
        doc.save(fileName);
      } catch (error) {
        console.error("PDF Generation Error:", error);
        this.showAlert("Error generating PDF report", "error");
      }
    },
    async exportToExcel() {
      // Add index column and AASB REF to Excel export with conditional label
      const refLabel = this.exportOptions.showFullRef ? "AASB REF" : "Case ID";
      const formattedData = this.previewData.map((row, index) => {
        // Special handling for subtotal rows
        if (row.isSubtotal) {
          const subtotalRow = {
            "No.": "", // Leave index empty for subtotal rows
            [refLabel]:
              row.subtotalLabel ||
              `Group: ${row.groupValue} (${row.count} items)`,
          };

          // Add empty cells for other columns to ensure proper formatting
          this.previewHeaders.forEach((header) => {
            if (header.value !== "id") {
              subtotalRow[header.text] =
                header.value === this.groupBy ? row.groupValue : "";
            }
          });
          return subtotalRow;
        }

        // Normal row handling
        const excelRow = {
          "No.": index + 1,
          [refLabel]: this.getFormattedRef(row),
        };
        this.previewHeaders.forEach((header) => {
          if (header.value !== "id") {
            // Skip 'id' column as we're using it for AASB REF
            excelRow[header.text] = row[header.value];
          }
        });
        return excelRow;
      });

      const ws = XLSX.utils.json_to_sheet(formattedData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Report");

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const fileName = `${this.reportName || "report"}_${moment().format(
        "YYYYMMDD"
      )}.xlsx`;
      saveAs(blob, fileName);
    },
    async exportToCsv() {
      // First column is No., second column is AASB REF
      const refLabel = this.exportOptions.showFullRef ? "AASB REF" : "Case ID";
      const headerCols = ["No.", refLabel];
      this.previewHeaders.forEach((h) => {
        if (h.value !== "id") {
          headerCols.push(h.text);
        }
      });
      const headers = headerCols.join(",");
      const rows = this.previewData.map((row, index) => {
        // Special handling for subtotal rows
        if (row.isSubtotal) {
          const rowValues = [
            "", // Empty index for subtotal
            `"${
              row.subtotalLabel ||
              `Group: ${row.groupValue} (${row.count} items)`
            }"`,
          ];

          // Add empty values for other columns except for the grouped column
          this.previewHeaders.forEach((header) => {
            if (header.value === "id") return;
            rowValues.push(
              header.value === this.groupBy ? `"${row.groupValue}"` : ""
            );
          });

          return rowValues.join(",");
        }

        // Normal row handling
        const rowValues = [
          index + 1, // No. column
          `"${this.getFormattedRef(row)}"`,
        ];

        // ...existing value formatting code...
        this.previewHeaders.forEach((header) => {
          if (header.value === "id") return; // Skip 'id' as we're using it for AASB REF
          // Handle commas in values to prevent CSV corruption
          const value = row[header.value]?.toString() || "";
          rowValues.push(value.includes(",") ? `"${value}"` : value);
        });
        return rowValues.join(",");
      });
      const csv = [headers, ...rows].join("\n");
      const fileName = `${this.reportName || "report"}_${moment().format(
        "YYYYMMDD"
      )}.csv`;

      // Save file
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, fileName);
    },
    // Add new helper methods for data formatting
    formatData(data) {
      return data.map((item) => {
        // Safely calculate days running
        const assignDate = moment(String(item.dateOfAssign));
        const daysRunning = assignDate.isValid()
          ? moment().diff(assignDate, "days")
          : 0;
        return {
          ...item,
          // Format IDs to their display values
          deptName: this.getDeptName(item.refType),
          subDeptCode: this.getSubDeptCode(item.subRefType),
          branchCode: this.getBranchCode(item.branch),
          insurerName: this.getInsurerName(item.insurer),
          handlerName: this.getHandlerName(item.handler),
          adjusterName: this.getUserFullName(item.adjuster),
          editorName: this.getUserFullName(item.editor),
          clerkName: this.getUserFullName(item.clerkInCharge),
          stateName: this.getStateName(item.stateOfLoss),
          statusName: this.getStageName(item.fileStatus),
          status: item.flag === 1 ? "Active" : "Inactive",
          // Format all dates with safe parsing
          dateOfAssign: this.formatDate(item.dateOfAssign),
          dateOfLoss: this.formatDate(item.dateOfLoss),
          dateStartInv: this.formatDate(item.dateStartInv),
          dateOfClose: this.formatDate(item.dateOfClose),
          createdAt: this.formatDate(item.createdAt),
          updatedAt: this.formatDate(item.updatedAt),
          // Format strings
          claimNo: item.claimNo?.toUpperCase() || "",
          vehicleNo: item.vehicleNo?.toUpperCase() || "",
          days: daysRunning,
        };
      });
    },
    formatDate(date) {
      if (!date) return "";
      // If it's already formatted properly, return it
      if (typeof date === "string" && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date)) {
        return date;
      }
      try {
        // Parse the date safely with multiple format attempts
        const parsed = moment(String(date));
        if (parsed.isValid()) {
          return parsed.format("DD/MM/YYYY");
        }
        return "";
      } catch (e) {
        console.error("Error formatting date:", e);
        return "";
      }
    },
    // Add this method to safely parse dates
    parseDate(dateString) {
      if (!dateString) return null;
      // Try parsing with specific formats first to avoid warnings
      const formats = [
        "YYYY-MM-DD",
        "DD/MM/YYYY",
        "MM/DD/YYYY",
        "YYYY/MM/DD",
        "DD-MM-YYYY",
      ];
      for (const format of formats) {
        const parsed = moment(String(dateString), format, true);
        if (parsed.isValid()) {
          return parsed;
        }
      }
      // Fallback to flexible parsing but with a strict flag to avoid warnings
      const parsed = moment(String(dateString));
      return parsed.isValid() ? parsed : null;
    },
    // Helper methods for getting display values
    getDeptName(id) {
      return this.refTypes.find((dept) => dept.id === id)?.name || "-";
    },
    getSubDeptCode(id) {
      return this.subRefTypes.find((sub) => sub.id === id)?.subCode || "-";
    },
    getBranchCode(id) {
      return this.branches.find((branch) => branch.id === id)?.brCode || "-";
    },
    getInsurerName(id) {
      return this.insurers.find((ins) => ins.id === id)?.name || "-";
    },
    getHandlerName(id) {
      return this.handlers.find((handler) => handler.id === id)?.name || "-";
    },
    getUserFullName(id) {
      return this.users.find((user) => user.id === id)?.fullname || "-";
    },
    getStateName(id) {
      return this.states.find((state) => state.id === id)?.name || "-";
    },
    getStageName(stageCode) {
      return (
        this.stages.find((stage) => stage.stageCode === stageCode)?.name || "-"
      );
    },
    // Add a new method to safely format dates
    getSafeDateFormat(dateValue) {
      if (!dateValue) return "";
      // If it's already formatted as DD/MM/YYYY, return it as is
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateValue)) {
        return dateValue;
      }
      // Use our consistent parseDate method
      const parsed = this.parseDate(dateValue);
      return parsed ? parsed.format("DD/MM/YYYY") : "";
    },
    // Add a helper method to format AASB Reference
    formatAARef(item) {
      // Handle subtotal rows specially
      if (item.isSubtotal) {
        return item.subtotalLabel || `Group: ${item.groupValue}`;
      }

      // Format the reference according to the pattern: AA/THEFT/ODI/194138/23/JB
      if (!item) return "";

      // ...existing code...
      const deptName =
        this.getDeptName(item.refType) !== "N/A"
          ? this.getDeptName(item.refType)
          : "-";

      const subDeptCode =
        this.getSubDeptCode(item.subRefType) !== "N/A"
          ? this.getSubDeptCode(item.subRefType)
          : "-";

      const branchCode =
        this.getBranchCode(item.branch) !== "N/A"
          ? this.getBranchCode(item.branch)
          : "-";

      const id = item.id || "-";
      // Get year from dateOfAssign, not from yearOnly function
      let year = "-";
      if (item.dateOfAssign) {
        try {
          // First try to parse it as a date object
          const dateObj = new Date(item.dateOfAssign);
          if (!isNaN(dateObj.getFullYear())) {
            year = dateObj.getFullYear().toString().substr(-2);
          } else {
            // If dateOfAssign is already a formatted string like "DD/MM/YYYY"
            const dateParts = item.dateOfAssign.split(/[/\-.]/);
            if (dateParts.length === 3) {
              // Check if it's in DD/MM/YYYY format
              if (dateParts[2].length === 4) {
                year = dateParts[2].substr(-2);
              }
            }
          }
        } catch (e) {
          console.error("Error parsing date:", e);
          year = "-";
        }
      }

      let reference = `AA`;
      if (deptName !== "-") reference += `/${deptName}`;
      if (subDeptCode !== "-") reference += `/${subDeptCode}`;
      if (id !== "-") reference += `/${id}`;
      if (year !== "-") reference += `/${year}`; // Make sure year is included here
      if (branchCode !== "-") reference += `/${branchCode}`;
      return reference;
    },
    // Add this helper method that returns either full ref or simple ID
    getFormattedRef(row) {
      // Handle subtotal rows specially
      if (row.isSubtotal) {
        return row.subtotalLabel || `Group: ${row.groupValue}`;
      }

      // Normal formatting for regular rows
      if (this.exportOptions.showFullRef) {
        return this.formatAARef(row);
      } else {
        return row.id?.toString() || "";
      }
    },
    // Add a custom filter method for vehicle numbers
    customVehicleFilter(item, queryText) {
      return item.toLowerCase().indexOf(queryText.toLowerCase()) > -1;
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<style scoped>
.v-card-title {
  font-size: 1.1rem;
}

.subtitle-1 {
  font-size: 0.9rem;
}

.v-container {
  padding: 12px;
}

.v-card {
  border-radius: 4px;
}

.v-tabs {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

@media (max-width: 600px) {
  .v-card-title {
    font-size: 1rem;
  }

  .subtitle-1 {
    font-size: 0.9rem;
  }
}
</style>
